import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Metrics } from 'components/insights/constants';
import { formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

import { buildEntityIdMap, buildEntityNameColumn, CELL_PADDING, CELL_PADDING_LEFT, renameNoneEntity } from './shared';

export type DischargesRow = {
  entityName: string;
  entityId: string;
  discharges: number;
  readmissionRate: number | null;
};

export function buildDischargesRows(
  rollups: AnalyticsResponseRollupCollection,
  noneDisplayName?: string
): DischargesRow[] {
  const entityGrouping = rollups.groupings[1];
  if (!entityGrouping) return [];

  const idByName = buildEntityIdMap(rollups);
  const entityNames = entityGrouping.dimensions.getCurrentDimensionValuesByIndex(0);
  const discharges = entityGrouping.metrics.getCurrentSeries(Metrics.ID_COUNT);
  const readmissionRates = entityGrouping.metrics.getCurrentSeries(Metrics.READMISSION_RATE);

  return entityNames
    .map((name, i) => ({
      entityName: renameNoneEntity(name, noneDisplayName),
      entityId: idByName.get(name) ?? '',
      discharges: discharges[i] ?? 0,
      readmissionRate: readmissionRates[i] ?? null,
    }))
    .filter((row) => row.entityName);
}

export const DISCHARGES_DEFAULT_SORT = { id: 'discharges', desc: true };

export function buildDischargesColumns(
  entityHeader: string,
  onEntityClick: (entityId: string, entityName: string) => void
): ColumnDef<DischargesRow, any>[] {
  const helper = createColumnHelper<DischargesRow>();

  return [
    buildEntityNameColumn<DischargesRow>(entityHeader, onEntityClick),
    helper.accessor('discharges', {
      header: 'Total Discharges',
      cell: (info) => formatWholeNumber(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor('readmissionRate', {
      header: 'Readmission Rate (%)',
      cell: (info) => formatPercentage(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}
